import {
  type ActivateIntegrationProviderRequest,
  type IntegrationCapabilityDescriptor,
  type IntegrationCapabilityStatusDto,
  type IntegrationProbeStatus,
  type IntegrationsOverviewDto,
  type WriteIntegrationConfigRequest,
} from '@aysar/contracts';
import { createApi } from '@reduxjs/toolkit/query/react';

import { EVENT_BUS } from '@/lib/event-bus';
import { baseQueryWithAuth } from '@/shared/store/middleware/base-query-with-auth/base-query-with-auth';

/**
 * The integrations vault, as far as the portal may see it (N21).
 *
 * Two requests build the whole panel: the **catalog** (capabilities → providers →
 * field descriptors) and the **overview** (what is configured, by whom, and which
 * provider is active). Every form on this screen is generated from the first, so
 * adding a provider on the api needs no admin code at all.
 *
 * No endpoint here can return a secret, and none is asked to: a secret arrives as
 * `last4` and nothing else. Test-connection is a POST because it reaches a third
 * party and costs something.
 */
export const integrationsApi = createApi({
  reducerPath: 'integrationsApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['IntegrationCatalog', 'IntegrationsOverview'],
  endpoints: (builder) => ({
    integrationCatalog: builder.query<
      { capabilities: readonly IntegrationCapabilityDescriptor[] },
      void
    >({
      query: () => ({ url: '/admin/integrations/catalog', method: 'GET' }),
      providesTags: ['IntegrationCatalog'],
    }),

    integrationsOverview: builder.query<IntegrationsOverviewDto, void>({
      query: () => ({ url: '/admin/integrations', method: 'GET' }),
      providesTags: ['IntegrationsOverview'],
    }),

    writeIntegrationConfig: builder.mutation<
      IntegrationCapabilityStatusDto,
      { capability: string; provider: string; body: WriteIntegrationConfigRequest }
    >({
      query: ({ capability, provider, body }) => ({
        url: `/admin/integrations/${capability}/${provider}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['IntegrationsOverview'],
      async onQueryStarted({ capability }, { queryFulfilled }) {
        try {
          await queryFulfilled;
          EVENT_BUS.publish({ type: 'integrations.config.changed', capability });
        } catch {
          // Refused — nothing to announce. Swallowed deliberately: `onQueryStarted`
          // has no caller, so a rejection escaping it becomes an unhandled
          // rejection. The failure is already the caller's, through `unwrap()`.
        }
      },
    }),

    activateIntegrationProvider: builder.mutation<
      IntegrationCapabilityStatusDto,
      { capability: string; body: ActivateIntegrationProviderRequest }
    >({
      query: ({ capability, body }) => ({
        url: `/admin/integrations/${capability}/activate`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['IntegrationsOverview'],
      async onQueryStarted({ capability }, { queryFulfilled }) {
        try {
          await queryFulfilled;
          EVENT_BUS.publish({ type: 'integrations.config.changed', capability });
        } catch {
          // See `writeIntegrationConfig`.
        }
      },
    }),

    /**
     * Tests one provider against the real service.
     *
     * A **mutation** rather than a query, and not because it writes: it reaches a
     * third party, costs money on some capabilities, and must never be re-run by a
     * cache refetch the operator did not ask for.
     *
     * It does now write, though — the result is stored per provider and served on
     * the overview as `lastProbe` (P10.5b), so the overview is invalidated or the
     * screen would keep showing the previous test until a reload. No
     * `integrations.config.changed` event: nothing about the configuration moved.
     *
     * P10.5c retires `useIntegrationsPageLogic`'s local `probes` state in favour of
     * `status.lastProbe`; until it does, the same fact lives in two places and only
     * the stored one survives a reload.
     */
    testIntegrationProvider: builder.mutation<
      IntegrationProbeStatus,
      { capability: string; provider: string }
    >({
      query: ({ capability, provider }) => ({
        url: `/admin/integrations/${capability}/${provider}/test`,
        method: 'POST',
      }),
      invalidatesTags: ['IntegrationsOverview'],
    }),
  }),
});

export const {
  useIntegrationCatalogQuery,
  useIntegrationsOverviewQuery,
  useWriteIntegrationConfigMutation,
  useActivateIntegrationProviderMutation,
  useTestIntegrationProviderMutation,
} = integrationsApi;
